import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { API_URL } from "../../config";

const number = (value, decimals = 0) => new Intl.NumberFormat("en-KE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
}).format(Number(value) || 0);

const longDate = (value) => {
    const date = value ? new Date(value) : new Date();
    if (Number.isNaN(date.getTime())) return value || "";
    return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

const shortDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value || "";
    return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};

// A few parts of one whole (members by gender, schools by type), each as a
// name, its number, its share and a bar as long as that share.
// A row may name its colour: "blue", "pink", "violet" or "grey".
function Breakdown({ rows, unit }) {
    const total = rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);

    if (total === 0) {
        return <p className="cf-muted cf-dash-empty">Nothing has been recorded yet.</p>;
    }

    return (
        <dl className="cf-breakdown">
            {rows.map((row) => {
                const value = Number(row.value) || 0;
                const share = (value / total) * 100;

                return (
                    <div key={row.name}>
                        <dt>{row.name}</dt>
                        <dd>
                            {number(value)} <span>{number(share)}%</span>
                        </dd>
                        <div className="cf-breakdown-bar" aria-hidden="true">
                            {value > 0 && <i className={row.colour ? `is-${row.colour}` : ""} style={{ width: `${share}%` }}></i>}
                        </div>
                    </div>
                );
            })}
            <div className="cf-breakdown-total">
                <dt>Total</dt>
                <dd>{number(total)} {unit}</dd>
            </div>
        </dl>
    );
}

// Prayer items longer than this are cut short, with three dots that open the rest.
const PRAYER_PREVIEW_LENGTH = 110;

// One recent mission feedback: the school, the number of converts and the prayer items.
function FeedbackItem({ feedback }) {
    const [open, setOpen] = useState(false);
    const prayer = (feedback.prayer_items || "").trim();
    const isLong = prayer.length > PRAYER_PREVIEW_LENGTH;
    const shown = isLong && !open ? prayer.slice(0, PRAYER_PREVIEW_LENGTH).trimEnd() : prayer;

    return (
        <li>
            <div className="cf-fb-head">
                <Link to={`/dashboard/missions/${feedback.mission_id}/feedback/details`}>
                    {feedback.school_name || "Unknown School"}
                </Link>
                <span className="cf-fb-converts">
                    <strong>{number(feedback.no_of_converts)}</strong> {Number(feedback.no_of_converts) === 1 ? "convert" : "converts"}
                </span>
            </div>
            <p className="cf-fb-prayer">
                <b>Prayer items:</b>{" "}
                {prayer ? shown : "None recorded."}
                {isLong && (
                    <button
                        type="button"
                        className="cf-more-dots"
                        onClick={() => setOpen(!open)}
                        aria-expanded={open}
                        aria-label={open ? "Show less of the prayer items" : "Show all the prayer items"}
                        title={open ? "Show less" : "Show more"}
                    >
                        {open ? "less" : "..."}
                    </button>
                )}
            </p>
        </li>
    );
}

// The first screen after signing in. The top is the ministry at a glance
// (members, missions, feedback and converts). Under it are the missions of the
// next 7 days beside the latest mission feedback, then the membership, school,
// book and event figures.
function Dashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem("access_token");
    const [upcomingMissions, setUpcomingMissions] = useState([]);
    const [loadingMissions, setLoadingMissions] = useState(false);
    const [recentFeedback, setRecentFeedback] = useState([]);
    const [loadingFeedback, setLoadingFeedback] = useState(false);
    const [loaded, setLoaded] = useState(false);

    // State to hold the statistics
    const [stats, setStats] = useState({
        activeUsers: 0,
        upcomingEvents: 0,
        pastEvents: 0,
        upcomingMissions: 0,
        pastMissions: 0,
        schoolsCount: 0,
        missionBooksCount: 0,
        genderDistribution: {
            male: 0,
            female: 0,
            unspecified: 0
        },
        schoolType: {
            girls: 0,
            boys: 0,
            mixed: 0
        },
        missionBooksAvailability: {
            available: 0,
            unavailable: 0,
            unspecified: 0
        },
        missionFeedback: {
            totalFeedbacks: 0,
            totalMissions: 0,
            feedbackPercentage: 0
        },
        converts: {
            totalConverts: 0,
            totalFeedbacks: 0,
            averagePerMission: 0
        }
    });

    const fetchDashboardStats = useCallback(async () => {
        try {
            if (!token) {
                navigate("/logout", { state: { from: location } });
                return;
            }

            const response = await fetch(`${API_URL}/dashboard-stats`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (response.ok) {
                const data = await response.json();
                setStats((previous) => ({ ...previous, ...data }));
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                console.error("Failed to fetch dashboard stats", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
        } finally {
            setLoaded(true);
        }
    }, [token, navigate, location]);

    const fetchUpcomingMissions = useCallback(async () => {
        setLoadingMissions(true);
        try {
            const response = await fetch(`${API_URL}/missions/upcoming?days=7`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUpcomingMissions(Array.isArray(data) ? data : []);
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            }
        } catch (error) {
            console.error("Error fetching upcoming missions:", error);
        } finally {
            setLoadingMissions(false);
        }
    }, [token, navigate, location]);

    const fetchRecentFeedback = useCallback(async () => {
        setLoadingFeedback(true);
        try {
            const response = await fetch(`${API_URL}/missions/recent-feedback?limit=5`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setRecentFeedback(Array.isArray(data) ? data : []);
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            }
        } catch (error) {
            console.error("Error fetching recent mission feedback:", error);
        } finally {
            setLoadingFeedback(false);
        }
    }, [token, navigate, location]);

    useEffect(() => {
        fetchDashboardStats();
        fetchUpcomingMissions();
        fetchRecentFeedback();
    }, [fetchDashboardStats, fetchUpcomingMissions, fetchRecentFeedback]);

    const feedback = stats.missionFeedback || {};
    const converts = stats.converts || {};
    const gender = stats.genderDistribution || {};
    const schoolType = stats.schoolType || {};
    const books = stats.missionBooksAvailability || {};

    return (
        <main aria-busy={!loaded}>
            <div className="cf-dash-head">
                <div>
                    <h1>The ministry at a glance</h1>
                    <p>{longDate()}</p>
                </div>
            </div>

            {/* At a glance */}
            <div className="cf-dash-kpis">
                <div className="cf-dash-kpi">
                    <small>Active members</small>
                    <strong>{number(stats.activeUsers)}</strong>
                    <em>with an active account</em>
                </div>
                <Link className="cf-dash-kpi" to="/dashboard/missions">
                    <small>Upcoming missions</small>
                    <strong>{number(stats.upcomingMissions)}</strong>
                    <em>{loadingMissions ? "..." : `${number(upcomingMissions.length)} in the next 7 days`}</em>
                </Link>
                <div className="cf-dash-kpi">
                    <small>Mission feedback</small>
                    <strong>{number(feedback.totalFeedbacks)} <span>/ {number(stats.pastMissions)}</span></strong>
                    <em className={Number(feedback.feedbackPercentage) < 100 ? "is-warn" : ""}>
                        {number(feedback.feedbackPercentage, 2)}% of past missions
                    </em>
                </div>
                <div className="cf-dash-kpi">
                    <small>Converts</small>
                    <strong>{number(converts.totalConverts)}</strong>
                    <em>
                        from {number(converts.totalFeedbacks)} missions, about {number(converts.averagePerMission, 2)} each
                    </em>
                </div>
            </div>

            <div className="cf-dash-panels">
                <section className="cf-dash-panel">
                    <div className="cf-dash-panel-head">
                        <h2>Upcoming missions, next 7 days</h2>
                        <Link to="/dashboard/missions">See all</Link>
                    </div>

                    {loadingMissions ? (
                        <p className="cf-muted cf-dash-empty">Loading...</p>
                    ) : upcomingMissions.length === 0 ? (
                        <p className="cf-muted cf-dash-empty">No missions scheduled in the next 7 days.</p>
                    ) : (
                        <div className="cf-dash-scroll">
                            <table className="cf-dash-table">
                                <thead>
                                    <tr><th>School</th><th>Mission</th><th>When</th><th>Status</th></tr>
                                </thead>
                                <tbody>
                                    {upcomingMissions.map((mission) => (
                                        <tr key={mission.id}>
                                            <td>
                                                <Link to={`/dashboard/missions/${mission.id}/details`}>
                                                    {mission.school?.name || 'Unknown School'}
                                                </Link>
                                            </td>
                                            <td>{mission.mission_type?.name || 'Mission'}</td>
                                            <td>
                                                {shortDate(mission.start_date)}
                                                {mission.start_time && <span className="cf-dash-when">{mission.start_time}</span>}
                                            </td>
                                            <td>
                                                <span className={`cf-badge ${mission.status === 'Active' ? 'is-ok' : 'is-pending'}`}>
                                                    {mission.status || 'Scheduled'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                <section className="cf-dash-panel">
                    <div className="cf-dash-panel-head">
                        <h2>Recent mission feedback</h2>
                        <Link to="/dashboard/missions">See all</Link>
                    </div>

                    {loadingFeedback ? (
                        <p className="cf-muted cf-dash-empty">Loading...</p>
                    ) : recentFeedback.length === 0 ? (
                        <p className="cf-muted cf-dash-empty">No mission feedback has been sent in yet.</p>
                    ) : (
                        <ul className="cf-fb-list">
                            {recentFeedback.map((feedback) => (
                                <FeedbackItem feedback={feedback} key={feedback.id} />
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            {/* Membership and the rest of the figures, kept small */}
            <div className="cf-dash-more">
                <section className="cf-dash-panel">
                    <h2>CFCM members by gender</h2>
                    <Breakdown
                        unit="members"
                        rows={[
                            { name: "Male", value: gender.male, colour: "blue" },
                            { name: "Female", value: gender.female, colour: "pink" },
                            { name: "Unspecified", value: gender.unspecified, colour: "grey" },
                        ]}
                    />
                </section>

                <section className="cf-dash-panel">
                    <div className="cf-dash-panel-head">
                        <h2>Schools by type</h2>
                        <Link to="/dashboard/schools">See all</Link>
                    </div>
                    <Breakdown
                        unit="schools"
                        rows={[
                            { name: "Boys", value: schoolType.boys, colour: "blue" },
                            { name: "Girls", value: schoolType.girls, colour: "pink" },
                            { name: "Mixed", value: schoolType.mixed, colour: "violet" },
                        ]}
                    />
                </section>

                <section className="cf-dash-panel">
                    <div className="cf-dash-panel-head">
                        <h2>Mission books availability</h2>
                        <Link to="/dashboard/books">See all</Link>
                    </div>
                    <Breakdown
                        unit="books"
                        rows={[
                            { name: "Available", value: books.available },
                            { name: "Unavailable", value: books.unavailable },
                            { name: "Unspecified", value: books.unspecified },
                        ]}
                    />
                </section>

                <section className="cf-dash-panel">
                    <h2>Missions and events</h2>
                    <table className="cf-dash-compare">
                        <thead>
                            <tr><th></th><th>Upcoming</th><th>Past</th></tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th><Link to="/dashboard/missions">Missions</Link></th>
                                <td>{number(stats.upcomingMissions)}</td>
                                <td>{number(stats.pastMissions)}</td>
                            </tr>
                            <tr>
                                <th><Link to="/dashboard/events">Events</Link></th>
                                <td>{number(stats.upcomingEvents)}</td>
                                <td>{number(stats.pastEvents)}</td>
                            </tr>
                        </tbody>
                    </table>
                </section>
            </div>
        </main>
    );
}

export default Dashboard;
